import React, { useEffect, useMemo, useState } from "react";
import { PrayerTimes } from "../../types/PrayerTypes";
import { ScreenData } from "../../types/ScreenTypes";
import { formatTimeDisplay } from "../../utils/timeUtils";

export interface AfterPrayerSalahChangesTemplateField {
  x: number;
  y: number;
  width: number;
  fontSize: number;
  fontWeight: number;
  color: string;
  fontFamily: string;
  align: "left" | "center" | "right";
}

export interface AfterPrayerSalahChangesTemplate {
  version: number;
  canvasWidth: number;
  canvasHeight: number;
  prayerName: AfterPrayerSalahChangesTemplateField;
  prayerTime: AfterPrayerSalahChangesTemplateField;
}

interface AfterPrayerSalahChangesTemplateOverlayProps {
  activePrayer: string;
  screenData: ScreenData;
  tomorrowPrayerTimes: PrayerTimes | null;
  duration: number;
  onComplete: () => void;
  template: AfterPrayerSalahChangesTemplate;
}

const PRAYER_META: Record<
  string,
  {
    customizedNameKey: string;
    prayerTimeKey: keyof PrayerTimes;
    fallbackName: string;
  }
> = {
  JamFajr: {
    customizedNameKey: "fajr",
    prayerTimeKey: "JamFajr",
    fallbackName: "Fajr",
  },
  JamDhuhr: {
    customizedNameKey: "dhuhr",
    prayerTimeKey: "JamDhuhr",
    fallbackName: "Dhuhr",
  },
  JamAsar: {
    customizedNameKey: "asar",
    prayerTimeKey: "JamAsar",
    fallbackName: "Asar",
  },
  JamEsha: {
    customizedNameKey: "isha",
    prayerTimeKey: "JamEsha",
    fallbackName: "Isha",
  },
};

function getTransformForAlign(align: "left" | "center" | "right") {
  if (align === "left") {
    return "translate(0, -50%)";
  }

  if (align === "right") {
    return "translate(-100%, -50%)";
  }

  return "translate(-50%, -50%)";
}

export default function AfterPrayerSalahChangesTemplateOverlay({
  activePrayer,
  screenData,
  tomorrowPrayerTimes,
  duration,
  onComplete,
  template,
}: AfterPrayerSalahChangesTemplateOverlayProps) {
  const [viewport, setViewport] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });

  useEffect(() => {
    const timer = window.setTimeout(onComplete, Math.max(1, duration) * 1000);
    return () => window.clearTimeout(timer);
  }, [activePrayer, duration, onComplete]);

  useEffect(() => {
    const handleResize = () => {
      setViewport({ width: window.innerWidth, height: window.innerHeight });
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const displayValues = useMemo(() => {
    const metadata = PRAYER_META[activePrayer];
    const customizedNames = screenData.customizedNames as
      | Record<string, string>
      | undefined;

    if (!metadata) {
      return { prayerName: activePrayer, prayerTime: "--:--" };
    }

    const prayerName =
      customizedNames?.[metadata.customizedNameKey] || metadata.fallbackName;
    const rawTime = tomorrowPrayerTimes?.[metadata.prayerTimeKey];
    const prayerTime = formatTimeDisplay(
      typeof rawTime === "string" ? rawTime : "--:--",
      !!screenData.use12HourFormat,
    );

    return { prayerName, prayerTime };
  }, [
    activePrayer,
    screenData.customizedNames,
    screenData.use12HourFormat,
    tomorrowPrayerTimes,
  ]);

  const scale = Math.min(
    viewport.width / template.canvasWidth,
    viewport.height / template.canvasHeight,
  );

  const renderField = (
    field: AfterPrayerSalahChangesTemplateField,
    text: string,
  ) => (
    <div
      style={{
        position: "absolute",
        left: `${(field.x / template.canvasWidth) * 100}%`,
        top: `${(field.y / template.canvasHeight) * 100}%`,
        width: `${(field.width / template.canvasWidth) * 100}%`,
        transform: getTransformForAlign(field.align),
        color: field.color,
        fontSize: `${Math.max(14, field.fontSize * scale)}px`,
        fontWeight: field.fontWeight,
        fontFamily: field.fontFamily,
        textAlign: field.align,
        lineHeight: 1.08,
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        textShadow: "0 2px 10px rgba(0, 0, 0, 0.35)",
      }}
    >
      {text}
    </div>
  );

  return (
    <div
      style={{
        position: "relative",
        width: "100%",
        height: "100%",
        overflow: "hidden",
        backgroundColor: "#000",
      }}
    >
      {screenData.afterPrayerSalahChangesBackgroundUrl ? (
        <img
          src={screenData.afterPrayerSalahChangesBackgroundUrl}
          alt=""
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover",
          }}
        />
      ) : null}

      <div
        style={{
          position: "absolute",
          inset: 0,
        }}
      >
        {renderField(template.prayerName, displayValues.prayerName)}
        {renderField(template.prayerTime, displayValues.prayerTime)}
      </div>
    </div>
  );
}
